import React, { createContext, useContext, useState, useEffect } from 'react';
import { storageKeys } from 'config/app.config';
import { User } from 'dtos/org/org.dto';
import { AuthService } from 'services/auth/auth.service';
import { isSupabaseAuthConfigured, supabase } from 'services/auth/supabase.client';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>(null as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const updateUser = (authUser: { id: string; email?: string; user_metadata?: Record<string, any>; app_metadata?: Record<string, any> } | null) => {
      if (!mounted) return;
      setUser(authUser ? {
        id: authUser.id,
        email: authUser.email || '',
        full_name: authUser.user_metadata?.full_name || authUser.email || '',
        role: authUser.app_metadata?.role || 'owner',
      } : null);
    };
    const subscription = supabase?.auth.onAuthStateChange((_event, session) => {
      updateUser(session?.user || null);
    });
    const restore = async () => {
      if (isSupabaseAuthConfigured && supabase) {
        const { data } = await supabase.auth.getSession();
        updateUser(data.session?.user || null);
      } else if (localStorage.getItem(storageKeys.accessToken)) {
        updateUser({ id: 'user-1', email: 'owner@democstore.com', user_metadata: { full_name: 'Alex Johnson' }, app_metadata: { role: 'owner' } });
      }
      if (mounted) setLoading(false);
    };
    void restore();
    return () => { mounted = false; subscription?.data.subscription.unsubscribe(); };
  }, []);

  const login = async (email: string, password: string) => {
    const authenticatedUser = await AuthService.login(email, password);
    setUser(authenticatedUser);
  };

  const register = async (name: string, email: string, password: string) => {
    const result = await AuthService.register(name, email, password);
    if (result.user) setUser(result.user);
    return result.requiresEmailConfirmation;
  };

  const logout = () => {
    AuthService.logout();
    localStorage.removeItem(storageKeys.currentStoreId);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

